// L'EFFACEUR — Banner cookies CNIL compliant
// Pas de cookies tiers tant que l'utilisateur n'a pas accepté.
// Granulaire : analytics / marketing / nécessaires (toujours actifs).
//
// Stockage : localStorage `cookie_consent` (version, choix, date)
// Si tu ajoutes Plausible/Meta Pixel/GA, lire ce flag avant d'init.

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

const CONSENT_VERSION = 'v1.0';
const CONSENT_KEY = 'leffaceur_cookie_consent';

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.version !== CONSENT_VERSION) return null;
    return data;
  } catch {
    return null;
  }
};

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      // Affiche après 1s pour pas casser l'UX immédiatement
      const t = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const save = (analyticsOk, marketingOk) => {
    const data = {
      version: CONSENT_VERSION,
      analytics: analyticsOk,
      marketing: marketingOk,
      necessary: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    setShow(false);
    // Reload pour activer les scripts conditionnels
    if (analyticsOk || marketingOk) {
      window.location.reload();
    }
  };

  const acceptAll = () => save(true, true);
  const refuseAll = () => save(false, false);
  const saveCustom = () => save(analytics, marketing);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50"
        >
          <div className="glass-card border border-cyan-500/30 p-5 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <Cookie className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-orbitron text-sm font-bold text-white mb-1">Cookies & confidentialité</h3>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Nous utilisons des cookies pour faire fonctionner le site (nécessaires) et,
                  avec votre accord, pour mesurer l'audience et vous proposer du contenu pertinent.
                </p>
              </div>
              <button onClick={refuseAll} className="text-gray-500 hover:text-gray-300" aria-label="Refuser tous les cookies">
                <X className="w-4 h-4" />
              </button>
            </div>

            {showDetails && (
              <div className="space-y-2 mb-4 mt-2 border-t border-gray-800 pt-3">
                <label className="flex items-center justify-between text-xs">
                  <span className="text-gray-300">Nécessaires (toujours actifs)</span>
                  <input type="checkbox" checked disabled className="cursor-not-allowed" />
                </label>
                <label className="flex items-center justify-between text-xs cursor-pointer">
                  <span className="text-gray-300">Analytics (Plausible)</span>
                  <input type="checkbox" checked={analytics} onChange={e => setAnalytics(e.target.checked)} />
                </label>
                <label className="flex items-center justify-between text-xs cursor-pointer">
                  <span className="text-gray-300">Marketing (publicité ciblée)</span>
                  <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} />
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={acceptAll} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 text-xs flex-1">
                Tout accepter
              </Button>
              <Button onClick={refuseAll} size="sm" variant="outline" className="text-xs flex-1">
                Tout refuser
              </Button>
              <Button onClick={() => showDetails ? saveCustom() : setShowDetails(true)} size="sm" variant="outline" className="text-xs">
                {showDetails ? 'Valider' : 'Personnaliser'}
              </Button>
            </div>

            <p className="text-[10px] text-gray-600 mt-3">
              <Link to="/confidentialite" className="hover:text-cyan-400 underline">En savoir plus</Link>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
